import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { cva } from 'class-variance-authority';
/**
 * Input Component (Layer 1 Primitive)
 *
 * A flexible text input component with validation states, labels, and helper text.
 * Built on design tokens for consistent theming and accessibility.
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   placeholder="Enter your email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   validationState="error"
 *   helperText="Please enter a valid email address"
 * />
 * ```
 */
const inputVariants = cva(
// Base styles
'w-full rounded-lg border px-3 py-2.5 text-base transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-uw-interactive-primary focus:ring-offset-2 focus:border-2 dark:shadow-inner disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-uw-bg-tertiary/50', {
    variants: {
        validationState: {
            default: 'border-uw-border-default bg-uw-bg-secondary text-uw-text-primary placeholder:text-uw-text-tertiary hover:border-uw-border-strong focus:border-uw-interactive-primary',
            error: 'border-uw-error bg-uw-bg-secondary text-uw-text-primary placeholder:text-uw-text-tertiary focus:ring-uw-error focus:border-uw-error',
            success: 'border-uw-success bg-uw-bg-secondary text-uw-text-primary placeholder:text-uw-text-tertiary focus:ring-uw-success focus:border-uw-success',
            warning: 'border-uw-warning bg-uw-bg-secondary text-uw-text-primary placeholder:text-uw-text-tertiary focus:ring-uw-warning focus:border-uw-warning',
        },
        size: {
            sm: 'px-2.5 py-1.5 text-sm',
            md: 'px-3 py-2.5 text-base',
            lg: 'px-4 py-3 text-lg',
        },
    },
    defaultVariants: {
        validationState: 'default',
        size: 'md',
    },
});
export const Input = React.forwardRef(({ label, validationState = 'default', helperText, size = 'md', leftIcon, rightIcon, labelHidden = false, required = false, className = '', id, disabled, readOnly, ...props }, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${React.useId()}`;
    const helperTextId = helperText ? `${inputId}-helper` : undefined;
    // Determine ARIA attributes based on validation state
    const ariaInvalid = validationState === 'error' ? true : undefined;
    const ariaDescribedBy = helperTextId;
    return (_jsxs("div", { className: "w-full", children: [label && (_jsxs("label", { htmlFor: inputId, className: `block mb-2 text-sm font-semibold text-uw-text-primary transition-colors ${labelHidden ? 'sr-only' : ''}`, children: [label, required && (_jsx("span", { className: "text-uw-error ml-1", "aria-label": "required", children: "*" }))] })), _jsxs("div", { className: "relative", children: [leftIcon && (_jsx("div", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-uw-text-tertiary pointer-events-none", children: leftIcon })), _jsx("input", { ref: ref, id: inputId, disabled: disabled, readOnly: readOnly, required: required, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, className: `${inputVariants({ validationState, size })} ${leftIcon ? 'pl-10' : ''} ${rightIcon ? 'pr-10' : ''} ${className}`, ...props }), rightIcon && (_jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2 text-uw-text-tertiary pointer-events-none", children: rightIcon }))] }), helperText && (_jsx("p", { id: helperTextId, className: `mt-2 text-sm transition-colors ${validationState === 'error'
                    ? 'text-uw-error'
                    : validationState === 'success'
                        ? 'text-uw-success'
                        : validationState === 'warning'
                            ? 'text-uw-warning'
                            : 'text-uw-text-secondary'}`, children: helperText }))] }));
});
Input.displayName = 'Input';
//# sourceMappingURL=Input.js.map